import { Component, inject, signal, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CrmService } from '../../shared/services/crm';
import { Specialist } from '../../shared/models/specialist.model';

@Component({
  selector: 'app-team',
  standalone: true,
  imports: [RouterLink],
  template: `
    <main class="max-w-7xl mx-auto px-8 py-32">
      <!-- Hero Section -->
      <header class="mb-16 text-center max-w-3xl mx-auto">
        <h1 class="font-serif text-5xl text-on-surface mb-6">Our Specialists</h1>
        <p class="font-sans text-xl text-on-surface-variant">Discover the elite professionals who deliver the Regency House standard of excellence to your estate. Each specialist is exhaustively vetted and comprehensively certified to ensure your absolute peace of mind.</p>
      </header>

      <!-- Sync Status Bar -->
      <div class="flex items-center justify-end mb-8 gap-3">
        @if (isSyncing()) {
          <span class="flex items-center gap-2 text-sm text-on-surface-variant">
            <span class="material-symbols-outlined text-gold-accent animate-spin text-base">sync</span>
            
          </span>
        } @else if (syncError()) {
          <span class="flex items-center gap-2 text-sm text-amber-600">
            <span class="material-symbols-outlined text-base">warning</span>
            CRM unavailable — showing cached profiles
          </span>
        } @else {
          <span class="flex items-center gap-2 text-sm text-emerald-600">
            <span class="material-symbols-outlined text-base" style="font-variation-settings: 'FILL' 1;">check_circle</span>
            Profiles synced from CRM
          </span>
        }
        <button
          class="flex items-center gap-1 px-4 py-2 border border-stone-200 bg-white text-xs font-bold text-on-surface hover:bg-stone-50 transition-colors disabled:opacity-50"
          (click)="sync()"
          [disabled]="isSyncing()">
          <span class="material-symbols-outlined text-base">sync</span>
          Sync Profiles
        </button>
      </div>

      <!-- Profile Grid -->
      @if (isSyncing() && specialists().length === 0) {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          @for (_ of [1, 2, 3]; track $index) {
            <div class="bg-white border border-stone-200 p-8 flex flex-col items-center animate-pulse">
              <div class="w-48 h-48 rounded-full bg-stone-100 mb-8"></div>
              <div class="h-6 bg-stone-100 rounded w-40 mb-3"></div>
              <div class="h-4 bg-stone-100 rounded w-56 mb-6"></div>
              <div class="w-full h-px bg-stone-100 mb-6"></div>
              <div class="flex gap-2 mb-8">
                <div class="h-8 bg-stone-100 rounded-full w-28"></div>
                <div class="h-8 bg-stone-100 rounded-full w-24"></div>
              </div>
              <div class="h-14 bg-stone-100 w-full"></div>
            </div>
          }
        </div>
      } @else {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          @for (specialist of specialists(); track specialist.id) {
            <div class="bg-white border border-stone-200 p-8 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow">
              <div class="relative mb-8">
                <div class="w-48 h-48 rounded-full overflow-hidden border-2 border-gold-accent p-1">
                  <img class="w-full h-full object-cover rounded-full" [src]="specialist.imageUrl" [alt]="specialist.name">
                </div>
                <div class="absolute bottom-2 right-2 bg-gold-accent text-white rounded-full p-2 flex items-center justify-center">
                  <span class="material-symbols-outlined text-xl" style="font-variation-settings: 'FILL' 1;">verified</span>
                </div>
              </div>
              <h2 class="font-serif text-3xl text-on-surface mb-2">{{ specialist.name }}</h2>
              <p class="font-sans font-bold text-sm text-gold-accent mb-6 tracking-widest uppercase">
                {{ specialist.title }} • {{ specialist.yearsExperience }} Years Exp.
              </p>
              <div class="w-full h-px bg-stone-100 mb-6"></div>
              <div class="flex flex-wrap justify-center gap-3 mb-8">
                @for (cert of specialist.certifications; track cert.label) {
                  <span class="inline-flex items-center px-4 py-2 border border-stone-200 bg-stone-50 rounded-full text-xs font-bold text-on-surface-variant">
                    <span class="material-symbols-outlined mr-2 text-gold-accent text-lg">{{ cert.icon }}</span>
                    {{ cert.label }}
                  </span>
                }
              </div>
              <p class="font-sans text-lg text-on-surface-variant mb-8 italic">"{{ specialist.quote }}"</p>
              @if (specialist.isPrimary) {
                <button class="w-full h-14 bg-on-surface text-white font-bold flex items-center justify-center gap-2 hover:bg-stone-800 transition-colors" routerLink="/book">
                  Request {{ specialist.name }} <span class="material-symbols-outlined">arrow_forward</span>
                </button>
              } @else {
                <button class="w-full h-14 border-2 border-on-surface text-on-surface font-bold flex items-center justify-center gap-2 hover:bg-stone-50 transition-colors" routerLink="/book">
                  Request {{ specialist.name }} <span class="material-symbols-outlined">arrow_forward</span>
                </button>
              }
            </div>
          }
        </div>
      }

      <!-- Trust Features Section -->
      <section class="mt-20 bg-stone-50 p-12 border border-stone-200">
        <div class="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent mb-4">security</span>
            <h3 class="font-bold text-sm text-on-surface mb-2 tracking-widest uppercase">Vetted & Bonded</h3>
            <p class="text-xs text-on-surface-variant">Every steward undergoes a rigorous background and reference check.</p>
          </div>
          <div class="text-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent mb-4">workspace_premium</span>
            <h3 class="font-bold text-sm text-on-surface mb-2 tracking-widest uppercase">Heritage Trained</h3>
            <p class="text-xs text-on-surface-variant">Mastery of fine materials, antiques, and high-end surface care.</p>
          </div>
          <div class="text-center">
            <span class="material-symbols-outlined text-4xl text-gold-accent mb-4">favorite</span>
            <h3 class="font-bold text-sm text-on-surface mb-2 tracking-widest uppercase">Safety First</h3>
            <p class="text-xs text-on-surface-variant">100% of our team is CPR and Emergency First Aid certified.</p>
          </div>
        </div>
      </section>
    </main>
  `,
  styles: [`
    :host { display: block; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .animate-spin { animation: spin 1s linear infinite; }
  `],
})
export class TeamComponent implements OnInit {
  private readonly crm = inject(CrmService);

  specialists = signal<Specialist[]>([]);
  isSyncing = signal(false);
  syncError = signal(false);

  ngOnInit(): void {
    this.sync();
  }

  sync(): void {
    this.isSyncing.set(true);
    this.syncError.set(false);
    this.crm.syncSpecialistProfiles().subscribe({
      next: (profiles) => {
        this.specialists.set(profiles);
        this.isSyncing.set(false);
      },
      error: () => {
        this.syncError.set(true);
        this.isSyncing.set(false);
      },
    });
  }
}
